import React, { useState, useEffect, useMemo } from 'react';
import styles from './WidenSearchBox.module.css';
import AssetCard from './AssetCard';
import { Loader } from '@strapi/design-system/Loader';
import Search from '@strapi/icons/Search';
import { EmptyStateLayout } from '@strapi/design-system/EmptyStateLayout';
import { useSelector, useDispatch } from 'react-redux';
import ExclamationMarkCircle from '@strapi/icons/ExclamationMarkCircle';
import Pagination from '../components/Pagination';
import PreviewModal from '../components/PreviewModal';
import { resetSearch, updateActivePage, updateResultsCount, updateQueryString, updateLastQueryString, resetActivePage } from '../redux/reducers/widenSearchSlice';
import { DEFAULT_RESULTS_PER_PAGE } from '../constants/pagination';
import FilterBox from '../components/FilterBox';
import FilterChip from '../components/FilterChip';
import sanitizeString from '../utils/sanitizeString';
import { SEARCH_BAR, WIDEN_MODAL_ENTRIES_FOUND_TITLE, WIDEN_MODAL_HEADER_TITLE_PREFIX, WIDEN_MODAL_HEADER_TITLE_SUFFIX, WIDEN_SEARCH_BOX_ERROR_MESSAGE, WIDEN_SEARCH_BOX_LOADING_MESSAGE, WIDEN_SEARCH_BOX_MODAL_INITIAL_STATE, WIDEN_SEARCH_BOX_NO_RESULTS_MESSAGE, WIDEN_SEARCH_BOX_PREFILTERS_METADATA, WIDEN_SEARCH_BOX_PREFILTERS_TYPE } from '../utils/constants';
import SearchInput from '../../../../../shared/admin/components/SearchInput/SearchInput';
import { Header } from '../../../../../shared/admin/components/Header/Header';
import Typography from '../../../../../shared/admin/components/common/Typography/Typography';
const WidenSearchBox = ({
  onSelect,
  filter
}) => {
  const [query, setQuery] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalData, setModalData] = useState(WIDEN_SEARCH_BOX_MODAL_INITIAL_STATE);
  const dispatch = useDispatch();
  const {
    activePage,
    resultsCount,
    queryString,
    lastQueryString,
    filters
  } = useSelector(state => state.widenSearch);
  useEffect(() => {
    return () => dispatch(resetSearch());
  }, []);
  useEffect(() => {
    if (query) {
      dispatch(updateResultsCount(query.total_count));
    }
  }, [query]);
  useEffect(() => {
    refetchData();
  }, [activePage]);
  const prefilters = useMemo(() => filter ? [{
    filterValue: filter,
    filterType: WIDEN_SEARCH_BOX_PREFILTERS_TYPE,
    filterMetadata: WIDEN_SEARCH_BOX_PREFILTERS_METADATA
  }] : [], [filter]);
  const getFiltersQuery = () => {
    const queries = [...prefilters, ...filters].map(({
      filterMetadata,
      filterValue
    }) => `${filterMetadata.query}:(${sanitizeString(filterValue)})`);
    return queries.join(', ');
  };
  const refetchData = currentQueryString => {
    const sanitizedString = sanitizeString(currentQueryString === undefined ? lastQueryString : currentQueryString);
    setError(null);
    return getAssetsFromWiden(`${getFiltersQuery()} ${sanitizedString}`);
  };
  const getAssetsFromWiden = async string => {
    try {
      const fetchWidenAssets = await import("../api/fetchWidenAssets");
      setQuery(null);
      setIsLoading(true);
      const data = await fetchWidenAssets.default(string, activePage);
      if (data?.query !== query?.query) {
        dispatch(resetActivePage());
      }
      setQuery(data);
    } catch (error) {
      console.log({
        error
      });
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };
  const maxPage = useMemo(() => Math.ceil(resultsCount / DEFAULT_RESULTS_PER_PAGE), [resultsCount]);
  const handlePageChange = input => {
    dispatch(updateActivePage(input));
  };
  const handleChangeQueryString = input => {
    dispatch(updateQueryString(input));
  };
  const handleSearch = value => {
    if (value !== undefined) {
      dispatch(updateLastQueryString(value));
      refetchData(value);
      return;
    }
    dispatch(updateLastQueryString(queryString));
    refetchData(queryString);
  };
  useEffect(() => {
    handleSearch();
  }, [filters]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, modalVisible && /*#__PURE__*/React.createElement(PreviewModal, {
    setModalVisible: setModalVisible,
    modalData: modalData,
    onSelect: onSelect
  }), /*#__PURE__*/React.createElement(Header, {
    titleRender: /*#__PURE__*/React.createElement("div", {
      className: styles['header-title-wrapper']
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "alpha",
      className: [styles['header-title'], styles['prefix']].join(' ')
    }, WIDEN_MODAL_HEADER_TITLE_PREFIX), /*#__PURE__*/React.createElement(Typography, {
      variant: "alpha",
      as: "h1",
      className: [styles['header-title'], styles['suffix']].join(' ')
    }, WIDEN_MODAL_HEADER_TITLE_SUFFIX)),
    subtitle: WIDEN_MODAL_ENTRIES_FOUND_TITLE(query?.total_count ?? 0),
    subtitleClassName: [styles['header'], styles['subtitle']].join(' '),
    noBack: true,
    className: styles.header
  }), /*#__PURE__*/React.createElement("div", {
    className: styles['search-controls']
  }, /*#__PURE__*/React.createElement(SearchInput, {
    className: styles['input-search'],
    onChange: handleChangeQueryString,
    onEnter: handleSearch,
    onReset: () => handleSearch(''),
    value: queryString,
    placeholder: SEARCH_BAR.placeholder
  }), /*#__PURE__*/React.createElement(FilterBox, {
    prefilters: prefilters
  }), filters.map(filter => /*#__PURE__*/React.createElement(FilterChip, {
    key: filter.filterType,
    filter: filter
  }))), !isLoading && (!resultsCount || error) && /*#__PURE__*/React.createElement("div", {
    className: styles['empty-state']
  }, /*#__PURE__*/React.createElement(EmptyStateLayout, {
    className: styles['empty-state'],
    icon: error ? /*#__PURE__*/React.createElement(ExclamationMarkCircle, null) : /*#__PURE__*/React.createElement(Search, null),
    content: error ? WIDEN_SEARCH_BOX_ERROR_MESSAGE : WIDEN_SEARCH_BOX_NO_RESULTS_MESSAGE
  })), isLoading && /*#__PURE__*/React.createElement("div", {
    className: styles.loader
  }, /*#__PURE__*/React.createElement(Loader, null, WIDEN_SEARCH_BOX_LOADING_MESSAGE)), query ? /*#__PURE__*/React.createElement("div", {
    className: styles['results-container']
  }, query.items.map((item, index) => /*#__PURE__*/React.createElement(AssetCard, {
    key: index,
    item: item,
    onSelect: onSelect,
    setModalVisible: setModalVisible,
    setModalData: setModalData
  }))) : '', resultsCount ? /*#__PURE__*/React.createElement(Pagination, {
    activePage: activePage,
    isLoading: isLoading,
    maxPage: maxPage,
    onPageChange: handlePageChange
  }) : '');
};
export default WidenSearchBox;